import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, apiUpload, fileUrl } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import FavPinButtons from "@/components/FavPinButtons";
import RowActions from "@/components/RowActions";
import SwipeableRow from "@/components/SwipeableRow";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { swallow } from "@/lib/log";
import { Plus, Paperclip, ExternalLink, X as XIcon } from "lucide-react";

/**
 * VerticalPage — shared list/add/edit page for the four life-verticals
 * (Investments, Insurance, Medical, Travel).
 *
 * Props:
 *   title, subtitle           — TopBar
 *   endpoint                  — e.g. "/investments"
 *   singularType              — e.g. "investment" (used for /favorites & /archive)
 *   icon (Lucide component)   — empty state icon + row default icon
 *   colorClass                — tailwind classes for row-avatar (e.g. "text-amber-500 bg-amber-500/12")
 *   emptySubtitle             — subtitle for empty state
 *   typeField                 — { name, label, options: [{value,label}] }
 *   sections                  — [{ title, fields: [{ name, label, type, placeholder }] }]
 *   formatMeta(item)          — returns { line1, line2, badge? } for row display
 */
export default function VerticalPage({
  title,
  subtitle,
  endpoint,
  singularType,
  icon: Icon,
  colorClass,
  emptySubtitle,
  typeField,
  sections,
  formatMeta,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null); // item being viewed
  const fileInputRef = useRef(null);

  const load = async () => {
    try { const { data } = await api.get(endpoint); setItems(data || []); }
    catch (e) { swallow(`Vertical:${singularType}`)(e); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  // ?new=1 deep-link to open the add sheet
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    if (sp.get("new") === "1") {
      openNew();
      const next = new URLSearchParams(sp);
      next.delete("new");
      setSp(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [sp]);

  const openNew = () => {
    setEditing(null);
    setForm({});
    setFile(null);
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    // Pre-populate every declared field from the item
    const seed = {};
    if (typeField) seed[typeField.name] = it[typeField.name] || "";
    for (const sec of sections) for (const f of sec.fields) seed[f.name] = it[f.name] ?? "";
    setForm(seed);
    setFile(null);
    setOpen(true);
  };

  const setField = (name, v) => setForm((f) => ({ ...f, [name]: v }));

  const requiredFields = useMemo(() => {
    const req = [];
    for (const sec of sections) for (const f of sec.fields) if (f.required) req.push(f);
    return req;
  }, [sections]);

  const save = async () => {
    // Validate required
    for (const rf of requiredFields) {
      const v = form[rf.name];
      if (!v || (typeof v === "string" && !v.trim())) {
        toast.error(`${rf.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      let saved = null;
      if (editing) {
        // JSON PATCH (metadata only). Attachment is replaced via a separate call below.
        const body = { ...form };
        // Coerce numbers
        for (const sec of sections) for (const f of sec.fields) {
          if (f.type === "number" && body[f.name] !== "" && body[f.name] !== null && body[f.name] !== undefined) {
            body[f.name] = Number(body[f.name]);
          }
        }
        const { data } = await api.patch(`${endpoint}/${editing.id}`, body);
        saved = data;
        if (file) {
          const fd = new FormData();
          fd.append("file", file);
          saved = await apiUpload(`${endpoint}/${editing.id}/attach`, fd);
        }
        toast.success(`${title} updated`);
      } else {
        // Multipart POST accepts item_type via a shared name.
        const fd = new FormData();
        if (typeField) fd.append("item_type", form[typeField.name] || "");
        for (const sec of sections) for (const f of sec.fields) {
          const v = form[f.name];
          if (v !== undefined && v !== null && v !== "") fd.append(f.postName || f.name, String(v));
        }
        if (file) fd.append("file", file);
        saved = await apiUpload(endpoint, fd);
        toast.success(`${title.slice(0, -1)} saved`);
      }
      // Optimistic list update so the item shows up instantly even if the
      // SW returns a stale list on the reload below.
      if (saved && saved.id) {
        setItems((prev) => {
          if (editing) return prev.map((x) => x.id === saved.id ? saved : x);
          return prev.some((x) => x.id === saved.id) ? prev : [saved, ...prev];
        });
      }
      setOpen(false);
      load(); // background reconciliation
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // RowActions renders the delete-with-undo toast; here we just do the API + UI update.
  const del = async (it) => {
    setItems((arr) => arr.filter((x) => x.id !== it.id));
    try { await api.delete(`${endpoint}/${it.id}`); }
    catch { toast.error("Delete failed — restored"); load(); }
  };

  const detachFile = async () => {
    if (!detail) return;
    try {
      const { data } = await api.delete(`${endpoint}/${detail.id}/attach`);
      setDetail(data);
      load();
      toast.success("Attachment removed");
    } catch {
      toast.error("Failed to remove attachment");
    }
  };

  return (
    <>
      <TopBar
        title={title}
        subtitle={subtitle}
        back
        showSearch
        right={
          <button
            onClick={openNew}
            className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
            data-testid={`${singularType}-add-button`}
            aria-label={`Add ${singularType}`}
          >
            <Plus className="h-4 w-4" />
          </button>
        }
      />

      <div className="px-4 py-4 space-y-3" data-testid={`${singularType}-page`}>
        {items.length === 0 && (
          <EmptyState
            icon={Icon}
            title={`No ${title.toLowerCase()} yet`}
            subtitle={emptySubtitle || `Tap + to add your first ${singularType}.`}
          />
        )}

        {items.map((it) => {
          const meta = formatMeta ? formatMeta(it) : {};
          return (
            <SwipeableRow
              key={it.id}
              testId={`swipe-${singularType}-${it.id}`}
              onSwipeLeft={async () => {
                setItems((arr) => arr.filter((x) => x.id !== it.id));
                try {
                  await api.post("/archive/toggle", { type: singularType, id: it.id, archived: true });
                  toast.success(`${title.slice(0, -1)} archived`);
                } catch { toast.error("Archive failed"); load(); }
              }}
              onSwipeRight={async () => {
                const next = !it.is_favorite;
                setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, is_favorite: next } : x));
                try {
                  await api.post("/favorites/toggle", { type: singularType, id: it.id, is_favorite: next });
                  toast.success(next ? "Added to Favorites" : "Removed from Favorites");
                } catch { toast.error("Update failed"); load(); }
              }}
            >
              <div className="rounded-2xl border border-border bg-card p-3" data-testid={`${singularType}-item`}>
                <div className="flex items-start gap-3">
                  <div className={cn("h-10 w-10 rounded-xl grid place-items-center shrink-0", colorClass)}>
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </div>
                  <button onClick={() => setDetail(it)} className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="font-display font-semibold text-[14px] leading-tight truncate">{it.title}</div>
                      {meta.badge && (
                        <Badge variant="secondary" className="h-4 px-1.5 rounded-full text-[9px] uppercase tracking-wider">{meta.badge}</Badge>
                      )}
                    </div>
                    {meta.line1 && <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{meta.line1}</div>}
                    {meta.line2 && <div className="text-[10px] text-muted-foreground/80 truncate">{meta.line2}</div>}
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <FavPinButtons
                      type={singularType}
                      id={it.id}
                      isFavorite={!!it.is_favorite}
                      pinnedAt={it.pinned_at}
                      size="sm"
                      onChanged={(u) => setItems((arr) => arr.map((x) => x.id === it.id ? { ...x, ...u } : x))}
                      onArchived={(id) => setItems((arr) => arr.filter((x) => x.id !== id))}
                    />
                    <RowActions
                      size="sm"
                      testId={`${singularType}-${it.id}`}
                      deleteLabel={`Delete ${singularType}`}
                      onEdit={() => openEdit(it)}
                      onDelete={() => del(it)}
                    />
                  </div>
                </div>
              </div>
            </SwipeableRow>
          );
        })}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${singularType}` : `New ${singularType}`}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {typeField && (
              <div className="space-y-1.5">
                <Label className="text-xs">{typeField.label}</Label>
                <Select
                  value={form[typeField.name] || ""}
                  onValueChange={(v) => setField(typeField.name, v)}
                >
                  <SelectTrigger data-testid={`${singularType}-type-select`}>
                    <SelectValue placeholder={`Choose ${typeField.label.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {typeField.options.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {sections.map((sec) => (
              <div key={sec.title} className="space-y-2">
                {sec.title && (
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{sec.title}</div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {sec.fields.map((f) => {
                    const val = form[f.name] ?? "";
                    const setV = (v) => setField(f.name, v);
                    return (
                      <div key={f.name} className={cn("space-y-1.5", f.colSpan === 2 && "col-span-2")}>
                        <Label className="text-xs">
                          {f.label}
                          {f.required && <span className="text-destructive ml-0.5">*</span>}
                        </Label>
                        {f.type === "textarea" ? (
                          <Textarea
                            rows={3}
                            placeholder={f.placeholder}
                            value={val}
                            onChange={(e) => setV(e.target.value)}
                            data-testid={`${singularType}-field-${f.name}`}
                          />
                        ) : (
                          <Input
                            type={f.type || "text"}
                            placeholder={f.placeholder}
                            value={val}
                            onChange={(e) => setV(e.target.value)}
                            data-testid={`${singularType}-field-${f.name}`}
                            inputMode={f.type === "number" ? "decimal" : undefined}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Attachment */}
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Attachment</div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid={`${singularType}-file-input`}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-1.5"
                  data-testid={`${singularType}-file-attach-button`}
                >
                  <Paperclip className="h-4 w-4" />
                  {file ? "Change file" : editing?.file_url ? "Replace file" : "Attach file"}
                </Button>
                {file && (
                  <div className="flex-1 min-w-0 text-xs text-muted-foreground truncate">{file.name}</div>
                )}
                {!file && editing?.file_url && (
                  <div className="flex-1 min-w-0 text-xs text-muted-foreground truncate">Existing file attached</div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`${singularType}-save-button`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail view */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display truncate">{detail?.title}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3">
              {typeField && detail[typeField.name] && (
                <Badge variant="secondary" className="rounded-full">
                  {typeField.options.find((o) => o.value === detail[typeField.name])?.label || detail[typeField.name]}
                </Badge>
              )}

              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                {sections.map((sec) => sec.fields.map((f) => {
                  const v = detail[f.name];
                  if (v === undefined || v === null || v === "") return null;
                  return (
                    <React.Fragment key={f.name}>
                      <dt className="text-xs text-muted-foreground">{f.label}</dt>
                      <dd className="text-sm break-words">{String(v)}</dd>
                    </React.Fragment>
                  );
                }))}
              </dl>

              {detail.file_url && (
                <div className="rounded-xl border border-border bg-card p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">Attachment</div>
                      <div className="text-[10px] text-muted-foreground truncate">{detail.mime_type || "file"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a
                      href={fileUrl(detail.file_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="h-8 w-8 rounded-full grid place-items-center hover:bg-[hsl(var(--accent))]"
                      aria-label="Open attachment"
                      data-testid={`${singularType}-detail-open-attach`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button
                      type="button"
                      onClick={detachFile}
                      className="h-8 w-8 rounded-full grid place-items-center hover:bg-[hsl(var(--accent))]"
                      aria-label="Remove attachment"
                      data-testid={`${singularType}-detail-remove-attach`}
                    >
                      <XIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
            {detail && (
              <Button onClick={() => { setDetail(null); openEdit(detail); }} data-testid={`${singularType}-detail-edit-button`}>Edit</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
